import React from "react";
import { connect } from "react-redux";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { cls } from "@wss/cls";
import { Image } from "@wss/image";
import Stars from "~/common/Stars";
import { RootState } from "~/common/Store/models/RootReadonly";
import { User } from "~/common/Store/models/User";
import { BannerType } from "../definitions";
import SpotlightRibbon, { RibbonType } from "../Ribbon";
import { SpotlightBannerItem } from "../../../ProductDetailPageResult";
import getLowestPrice from "~/ProductDetails/Pricing/getLowestPrice";
import { getDestinationItemNumber, sendCtrEvent } from "~/analytics/ClickThroughRateTracking/clickThroughRateHelpers";
interface ProductInfoProps {
  item: SpotlightBannerItem;
  bannerType: BannerType;
  mounted: boolean;
  user: User;
  displayDoubleProductBorder?: boolean;
  originalLinkingItemNumber: string;
  doubleProductItemNumbers?: string;
}
const ProductInfo = ({
  item,
  bannerType,
  user,
  originalLinkingItemNumber,
  displayDoubleProductBorder,
  doubleProductItemNumbers
}: ProductInfoProps) => {
  const displayViewProductMessage = item.price.displayMinimumAdvertisedPrice && !!item.price.minimumAdvertisedPriceProperties?.type?.trim();
  const displayPrice = getLowestPrice(item.price, item.showBelowMinimumAdvertisedPriceForLoggedInCustomers, item.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers, user, false);
  const ctrProps = {
    source: originalLinkingItemNumber,
    destination: getDestinationItemNumber(bannerType, item, doubleProductItemNumbers),
    type: bannerType
  };
  let bannerDescription = "";
  if (bannerType === BannerType.GoodBetterBest && item.ratingDescription) {
    bannerDescription = item.ratingDescription;
  }
  if (bannerType === BannerType.SideBySide) {
    if (item.bannerMessage) {
      bannerDescription = item.bannerMessage;
    }
    if (item.compareMessage && item.compareMessage?.length > 0) {
      bannerDescription = item.compareMessage;
    }
    if (item.isRecommendedSimilarItem && item.bannerDescription) {
      bannerDescription = item.bannerDescription;
    }
  }
  return <div className={cls("flex", "justify-start", "max-w-screen-lg", "min-w-85", "py-4", "px-4", "w-full", "sm:min-w-0", displayDoubleProductBorder ? "border-gray-200" : "lt:border-gray-200", displayDoubleProductBorder ? "border-solid" : "lt:border-solid", displayDoubleProductBorder ? "border-t-0" : "lt:border-t-0", displayDoubleProductBorder ? "border-r-0" : "lt:border-r-0", displayDoubleProductBorder ? "border-b-0" : "lt:border-b-0", displayDoubleProductBorder ? "border-l" : "lt:border-l")} data-sentry-component="ProductInfo" data-sentry-source-file="index.tsx">
      <div className="flex">
        <div className="flex-shrink-0">
          <a className="block" href={item.link} onClick={() => {
          sendCtrEvent("Click", ctrProps);
        }}>
            <Image className="w-20 h-20 lg:w-26 lg:h-26 border-gray-100 border-solid border box-border" src={item.imagePath} alt={item.description} height="98" width="98" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
          </a>
        </div>
        <div className="flex flex-col pl-4">
          <div className={`ribbon-stack ${"inline-flex"}`}>
            {bannerType === BannerType.GoodBetterBest && item.label && <SpotlightRibbon ribbonType={RibbonType[item.label]} adjacentBanner={false} />}
            {item.isWebstaurantPlusEligible && <SpotlightRibbon ribbonType={RibbonType.Plus} adjacentBanner={bannerType === BannerType.GoodBetterBest} />}
            {item.hasFreeShipping && <SpotlightRibbon ribbonType={RibbonType.FreeShipping} adjacentBanner={bannerType === BannerType.GoodBetterBest || item.isWebstaurantPlusEligible} />}
          </div>
          <a href={item.link} className="font-bold text-sm leading-tight overflow-hidden text-green-500 mb-1 hover:text-green-800" style={{
          maxHeight: "4.25rem"
        }} onClick={() => {
          sendCtrEvent("Click", ctrProps);
        }}>
            {item.description}
          </a>
          <div className="flex flex-col xl:flex-row">
            <p className="block mb-0">
              <span className="font-semibold text-base leading-none text-red-500">
                {displayViewProductMessage ? <span className="font-semibold text-sm leading-none text-gray-900">
                    View product for details
                  </span> : formatAsCurrency.formatIfUndefined(displayPrice)}
              </span>
              {!displayViewProductMessage && <span className="font-semibold text-xs leading-none text-gray-900">
                  /{item.unitOfMeasure}
                </span>}
            </p>
            <div className="inline-flex items-center">
              <div className="xl:ml-3 mr-1 mt-[-6px]">
                <Stars rating={item.overallRating} containerClasses="flex" data-sentry-element="Stars" data-sentry-source-file="index.tsx" />
              </div>
              <span className="font-normal text-xs text-gray-600">
                {item.ratingCount > 0 && <>
                    ({item.ratingCount})
                    <span className="sr-only"> reviews</span>
                  </>}
              </span>
            </div>
          </div>
          <p className="font-normal text-xs leading-tight mb-0 mt-1 mr-4 text-gray-900">
            {bannerDescription}
          </p>
        </div>
      </div>
    </div>;
};
export const mapStateToProps = (state: RootState, ownProps: ProductInfoProps) => {
  if (!ownProps.mounted) return {
    user: ownProps.user
  };
  return {
    user: state.user
  };
};
export default connect(mapStateToProps)(ProductInfo);